import React, { useContext, useState } from "react";
import { FilterArea, MicroBar, ScatterChart } from "@sia/style-guide";
import { MasterKeyContext } from "contexts/MasterKeyContext";
import { usePageRefresh } from "../tools";
import { getKeyFiguresCurrency, parseKpiScoreCardData } from "./data/dataParsing";
import { KPI_SCORECARD_TRANSLATION_KEYS, MINOR_KPI_TRANSLATION_KEYS } from "./kpiConfiguration";
import { getScatterDenominators } from "./scatterConfiguration";
import { hasScatterCompanyValues, parseScatterData } from "./data/scatterData";
import { formatScatterAxisValue, formatScatterValue, isScatterDifferenceZero } from "./data/scatterFormatters";
import KpiScorecardSection from "./KpiScorecardSection";
import "./KeyFiguresPage.scss";
import Legend from "../Shared/Legend/Legend";

export default function KeyFiguresPage({ translate, filters, setFilters, data, selectedTab, setSelectedTab, showCustomerData, setShowCustomerData }) {
  const KEYFIGURES_PAGE_TAB_INDEX = 0;

  usePageRefresh(selectedTab, KEYFIGURES_PAGE_TAB_INDEX, setSelectedTab);

  const { isMasterKeyValid } = useContext(MasterKeyContext);

  const kpiCards = parseKpiScoreCardData(data, translate);
  const minorCards = parseKpiScoreCardData(data, translate, "minor");

  const currency = getKeyFiguresCurrency(data);
  const scatterMetrics = parseScatterData(data, translate);
  const denominators = getScatterDenominators(translate, currency);

  const [selectedDenominator, setSelectedDenominator] = useState("totalFte");

  const activeDenominator = denominators.find((item) => item.id === selectedDenominator) || denominators[0];

  const canDisplayOwnData =
    Boolean(isMasterKeyValid) &&
    ([...kpiCards, ...minorCards].some((kpi) => kpi.respondentValue !== null) || hasScatterCompanyValues(scatterMetrics));

  const showOwnData = canDisplayOwnData && showCustomerData;

  return (
    <div className="benchmarking-key-container sia-padding-02-left sia-padding-02-right sia-padding-04-top">
      {filters && (
        <FilterArea
          translate={translate}
          initialFilters={filters}
          onFiltersUpdate={(filterAreaState) => {
            setFilters((state) => ({
              ...state,
              filters: filterAreaState,
              newest: true,
            }));
          }}
          widthFormula={"calc(100%)"}
        />
      )}

      <div className="sia-padding-02-bottom benchmarking-overheads-bar-container">
        {data?.reportData && (
          <MicroBar
            translate={translate}
            realData={data?.reportData?.businessKeyFigures?.[0]?.keyFigures?.[0]?.metrics?.coverage?.organicCategories}
            availableData={data?.reportData?.businessKeyFigures?.[0]?.keyFigures?.[0]?.metrics?.coverage?.totalCategories}
            weight={data?.reportData?.businessKeyFigures?.[0]?.keyFigures?.[0]?.metrics?.coverage?.organicWeightPercent}
            isSyntheticDataIncluded={data?.reportData?.syntheticDataIncluded}
          />
        )}
        <Legend
          canDisplayOwnData={canDisplayOwnData}
          showCustomerData={canDisplayOwnData && showCustomerData}
          setShowCustomerData={setShowCustomerData}
          translate={translate}
        />
      </div>
      <KpiScorecardSection
        title={translate(KPI_SCORECARD_TRANSLATION_KEYS.sectionTitle)}
        kpis={kpiCards}
        translate={translate}
        currency={currency}
        showOwnData={showOwnData}
      />

      <KpiScorecardSection
        title={translate(MINOR_KPI_TRANSLATION_KEYS.sectionTitle)}
        kpis={minorCards}
        size="compact"
        translate={translate}
        currency={currency}
        showOwnData={showOwnData}
      />
      <div className="sia-key-figures-scatter sia-padding-01-top">
        <ScatterChart
          translate={translate}
          title={activeDenominator.title}
          subtitle={activeDenominator.subtitle}
          metrics={scatterMetrics.filter((metric) => metric.id !== activeDenominator.identityMetric)}
          denominators={denominators}
          selectedDenominator={activeDenominator.id}
          onDenominatorChange={setSelectedDenominator}
          showCustomerData={showOwnData}
          denominatorLabel={translate("benchmarking.evaluations.key-figures.scatter.denominator")}
          unitLabel={translate("benchmarking.evaluations.benchmark.unit")}
          formatValue={formatScatterValue}
          formatDifference={formatScatterValue}
          isDifferenceZero={isScatterDifferenceZero}
          formatAxisValue={(value, denominator) => formatScatterAxisValue(value, denominator, currency)}
        />
      </div>
    </div>
  );
}
